import { useState } from 'react';
import { ChevronRight, Loader2 } from 'lucide-react';

/**
 * Reusable tool-call card for chat interfaces.
 *
 * Props:
 *  - tool      {string}  Tool name (e.g. "read_reporting_model")
 *  - status    {'running' | 'done' | 'error'}  Current execution status
 *  - result    {string?} Optional plain-text result to show when expanded
 *  - className {string?} Extra classes on the outer wrapper
 */
export default function ToolCallCard({ tool, status = 'running', result, className = '' }) {
  const [open, setOpen] = useState(false);
  const hasContent = !!result;

  return (
    <div
      className={`my-1 ${className}`}
      style={{
        background: '#fafaf9',
        border: '1px solid rgba(214, 211, 209, 0.6)',
        borderRadius: 10,
      }}
    >
      {/* Header row */}
      <button
        type="button"
        onClick={() => hasContent && setOpen(o => !o)}
        style={{ borderRadius: 10 }}
        className={`flex items-center gap-2.5 w-full px-3 py-2 text-left transition-colors ${hasContent ? 'cursor-pointer hover:bg-stone-100/60' : 'cursor-default'}`}
      >
        {/* Chevron */}
        <ChevronRight
          size={14}
          style={{ color: '#a8a29e', opacity: hasContent ? 1 : 0.35, transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 200ms' }}
          className="flex-shrink-0"
        />

        {/* Status dot / spinner */}
        {status === 'running' ? (
          <Loader2 size={11} className="animate-spin flex-shrink-0" style={{ color: '#1a7f64' }} />
        ) : (
          <span
            className="flex-shrink-0"
            style={{
              width: 8, height: 8, borderRadius: '50%',
              background: status === 'error' ? '#f87171' : '#10b981',
            }}
          />
        )}

        {/* Tool name */}
        <span style={{ fontSize: 13, fontFamily: "'SF Mono', Menlo, Monaco, monospace", color: '#44403c' }} className="truncate">
          {tool}
        </span>

        {/* Spacer */}
        <span className="flex-1" />

        {/* Status badge */}
        <span
          className="flex-shrink-0"
          style={{
            fontSize: 11, fontWeight: 500, padding: '2px 10px', borderRadius: 999, whiteSpace: 'nowrap',
            ...(status === 'error'
              ? { color: '#dc2626', background: '#fef2f2' }
              : status === 'done'
                ? { color: '#059669', background: '#ecfdf5' }
                : { color: '#1a7f64', background: 'rgba(26, 127, 100, 0.1)' }),
          }}
        >
          {status === 'error' ? 'Error' : status === 'done' ? 'Done' : 'Running'}
        </span>
      </button>

      {/* Expandable result */}
      {open && hasContent && (
        <div style={{ padding: '10px 16px', borderTop: '1px solid rgba(214,211,209,0.4)', background: '#fff', borderRadius: '0 0 10px 10px' }}>
          <pre style={{ fontSize: 12, fontFamily: "'SF Mono', Menlo, Monaco, monospace", color: '#57534e', whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 192, overflowY: 'auto', margin: 0 }}>
            {result}
          </pre>
        </div>
      )}
    </div>
  );
}
